import React from "react";
import { Menu } from "@headlessui/react";
import { MoreHorizontal } from "lucide-react";

/**
 * Reusable Dropdown Menu Component
 *
 * @param {Array} items - Array of menu items
 * Each item should have: { label, onClick, variant (optional: 'default' | 'danger'), divider (optional: boolean) }
 * @param {React.ReactNode} trigger - Optional custom trigger button
 * @param {string} triggerClassName - Optional className for trigger button
 *
 * @example
 * const menuItems = [
 *   { label: "Edit", onClick: () => handleEdit() },
 *   { label: "Make a copy", onClick: () => handleCopy() },
 *   { label: "Favorite", onClick: () => handleFavorite() },
 *   { divider: true },
 *   { label: "Delete", onClick: () => handleDelete(), variant: "danger" }
 * ];
 *
 * <DropdownMenu items={menuItems} />
 */

const DropdownMenu = ({
  items = [],
  trigger,
  triggerClassName = "p-2 rounded-lg hover:bg-gray-100 transition-colors",
  menuClassName = "absolute right-0 mt-2 w-60 origin-top-right bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10 py-1",
}) => {
  return (
    <Menu as="div" className="relative">
      <Menu.Button className={triggerClassName}>
        {trigger || (
          <MoreHorizontal className="h-5 w-5 text-gray-600" strokeWidth={1.5} />
        )}
      </Menu.Button>
      <Menu.Items className={menuClassName}>
        {items.map((item, index) => {
          // Render divider
          if (item.divider) {
            return (
              <div
                key={`divider-${index}`}
                className="border-t border-gray-100 my-1"
              ></div>
            );
          }

          // Render menu item
          return (
            <Menu.Item key={index}>
              {({ active }) => (
                <button
                  onClick={item.onClick}
                  className={`${
                    active ? "bg-gray-100" : ""
                  } flex items-center w-full px-4 py-2 text-sm whitespace-nowrap ${
                    item.variant === "danger" ? "text-red-600" : "text-gray-950"
                  }`}
                >
                  {item.icon && <span className="mr-2">{item.icon}</span>}
                  {item.label}
                </button>
              )}
            </Menu.Item>
          );
        })}
      </Menu.Items>
    </Menu>
  );
};

export default DropdownMenu;
